import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Appointment,
  AppointmentChatCompletionStatus,
  EncounterStatus,
} from 'src/models';
import { api } from 'src/services';
import {
  UseInfiniteQueryOverrideOptions,
  UseQueryOverrideOptions,
} from '../common';

export type UseGetAppointmentListParams = {
  patientId?: number;
  patientName?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
  chatCompletionStatus?: AppointmentChatCompletionStatus;
  endDate?: string;
  inVisitStatus?: EncounterStatus;
  practitionerIds?: number[];
  startDate?: string;
};

export type GetAppointmentListResponse = {
  Appointments: Appointment[];
  total: number;
  reminder_patient_appt_count: number;
  total_patient_appt_count: number;
};

const filterToParamsAndPayload = ({
  patientId,
  patientName,
  sortBy,
  sortDirection,
  page,
  perPage,
  chatCompletionStatus,
  endDate,
  inVisitStatus,
  practitionerIds,
  startDate,
}: UseGetAppointmentListParams) => {
  return {
    params: {
      sort_by: sortBy,
      sort_direction: sortDirection,
      page: page,
      per_page: perPage,
    },
    payload: {
      patient_id: patientId,
      patient_name: patientName,
      chat_completion_status: chatCompletionStatus,
      end_date: endDate,
      in_visit_status: inVisitStatus,
      practitioner_ids: practitionerIds,
      start_date: startDate,
    },
  };
};

const defaultPageSize = 50;

export const useGetAppointmentList = (
  filters: UseGetAppointmentListParams,
  options: UseQueryOverrideOptions = {}
) => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { params, payload } = filterToParamsAndPayload(filters);
  const isPayloadEmpty = Object.values(payload).every(
    (value) => value === undefined
  );
  return useQuery({
    ...options,
    queryKey: ['appointmentList', filters],
    queryFn: () =>
      isPayloadEmpty
        ? api
            .get<{
              appointments: Appointment[];
              total: number;
            }>(`/get_appointment/status?tz=${tz}`, { params })
            .then(({ data }) => ({
              appointments: data.appointments,
              total: data.total,
            }))
        : api
            .post<GetAppointmentListResponse>(
              `/get_appointment/filters?tz=${tz}`,
              {
                ...payload,
              },
              { params }
            )
            .then(({ data }) => ({
              appointments: data.Appointments,
              total: data.total,
            })),
  });
};

export const useGetAppointmentInfiniteList = (
  filters?: UseGetAppointmentListParams,
  options: UseInfiniteQueryOverrideOptions = {}
) => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return useInfiniteQuery({
    ...options,
    queryKey: ['appointmentList', 'infinite', filters],
    queryFn: ({ pageParam }) => {
      const { params, payload } = filterToParamsAndPayload(pageParam);
      return api
        .post<GetAppointmentListResponse>(
          `/get_appointment/filters?tz=${tz}`,
          {
            ...payload,
          },
          { params }
        )
        .then(({ data }) => ({
          appointments: data.Appointments || [],
          total: data.total,
        }));
    },
    getNextPageParam: (lastPage, _, lastPageParam) => {
      return lastPageParam.page * lastPageParam.perPage < lastPage.total
        ? {
            perPage: defaultPageSize,
            ...filters,
            page: lastPageParam.page + 1,
          }
        : undefined;
    },
    initialPageParam: {
      page: 1,
      perPage: defaultPageSize,
      ...filters,
    },
  });
};

export const useAppointmentListClient = () => {
  const queryClient = useQueryClient();

  const scanAppointment = (
    uuid: Appointment['appt_uuid'],
    replacer: (appointment: Appointment) => Appointment
  ) => {
    queryClient.setQueriesData<
      ReturnType<typeof useGetAppointmentList>['data']
    >({ queryKey: ['appointmentList'] }, (oldData) => {
      if (!oldData?.appointments?.length) return oldData;

      return {
        ...oldData,
        appointments: oldData.appointments.map((appointment) =>
          appointment.appt_uuid === uuid ? replacer(appointment) : appointment
        ),
      };
    });

    queryClient.setQueriesData<
      ReturnType<typeof useGetAppointmentInfiniteList>['data']
    >({ queryKey: ['appointmentList', 'infinite'] }, (oldData) => {
      if (!oldData?.pages?.length) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          appointments: page.appointments.map((appointment) =>
            appointment.appt_uuid === uuid ? replacer(appointment) : appointment
          ),
        })),
      };
    });
  };

  const deleteAppointment = ({ uuid }: { uuid: Appointment['appt_uuid'] }) => {
    queryClient.setQueriesData<
      ReturnType<typeof useGetAppointmentList>['data']
    >({ queryKey: ['appointmentList'] }, (oldData) => {
      if (!oldData?.appointments?.length) return oldData;

      return {
        ...oldData,
        appointments: oldData.appointments.filter(
          (appointment) => appointment.appt_uuid !== uuid
        ),
      };
    });

    queryClient.setQueriesData<
      ReturnType<typeof useGetAppointmentInfiniteList>['data']
    >({ queryKey: ['appointmentList', 'infinite'] }, (oldData) => {
      if (!oldData?.pages?.length) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          appointments: page.appointments.filter(
            (appointment) => appointment.appt_uuid !== uuid
          ),
        })),
      };
    });
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['appointmentList'] });
  };

  return {
    deleteAppointment,
    scanAppointment,
    invalidate,
  };
};
